import SunAccent from "@/components/feature/SunAccent";
import { Link } from "react-router-dom";
import Breadcrumbs, { type BreadcrumbItem } from "@/components/feature/Breadcrumbs";
import { farmCoverAlt } from "@/lib/imageAlt";
import { farmReopeningLabel, isFarmOpen, type Farm } from "@/mocks/farms";

interface FarmDetailHeroProps {
  farm: Farm;
  breadcrumbs: BreadcrumbItem[];
  /** Opens the enquiry form in "notify me" mode. Only used while the farm is closed. */
  onNotify?: () => void;
}

export default function FarmDetailHero({ farm, breadcrumbs, onNotify }: FarmDetailHeroProps) {
  const open = isFarmOpen(farm);
  const reopeningLabel = farmReopeningLabel(farm);
  const facts = [
    { icon: "ri-plant-line", label: "Main crop", value: farm.crop },
    { icon: "ri-ruler-2-line", label: "Farm size", value: farm.area },
    { icon: "ri-calendar-line", label: "Established", value: farm.established },
    { icon: "ri-map-pin-line", label: "Village", value: `${farm.village}, ${farm.district}` },
  ];

  return (
    <section className="relative min-h-[75vh] flex flex-col justify-end overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={farm.image}
          width={1920}
          height={1080}
          alt={farmCoverAlt(farm)}
          title={`${farm.name} - ${farm.crop} farm in ${farm.village}`}
          className="w-full h-full object-cover object-top"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/35 to-black/80"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 pt-28 md:pt-32 pb-10 md:pb-14">
        <Breadcrumbs
          items={breadcrumbs}
        />

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <SunAccent circleClassName="w-9 h-9" iconClassName="text-lg" />
          <span className="bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
            {farm.village}
          </span>
          {!open && (
            <span className="inline-flex items-center gap-1.5 bg-background-50 text-secondary-900 border border-secondary-300 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
              <i className="ri-time-line"></i>
              Reopening soon
            </span>
          )}
          <span className="text-xs md:text-sm text-background-100">
            Hosted by <span className="font-semibold text-background-50">{farm.farmer}</span>
          </span>
        </div>

        <h1 className="font-heading text-4xl md:text-6xl leading-tight text-background-50 font-semibold max-w-3xl">
          {farm.name}
        </h1>
        <p className="mt-4 text-base md:text-lg text-background-100 leading-relaxed max-w-2xl">
          {farm.summary}
        </p>

        {!open && (
          <div className="mt-6 max-w-2xl rounded-md border border-accent-400/50 bg-background-950/50 backdrop-blur-sm px-4 py-3.5 flex items-start gap-3">
            <i className="ri-information-line text-accent-300 text-lg shrink-0"></i>
            <p className="text-sm text-background-100 leading-relaxed">
              <span className="font-semibold text-background-50">This farm is closed for now.</span>{" "}
              {farm.name}{" "}
              {reopeningLabel
                ? `is expected back by ${reopeningLabel}, so dates cannot be booked yet.`
                : "is reopening soon, so dates cannot be booked yet."}{" "}
              Everything you see here is still real — ask us to tell you as soon as visits resume and
              you will hear from us first.
            </p>
          </div>
        )}

        <div className="mt-7 flex flex-wrap items-center gap-3">
          {open && (
            <Link
              to={`/experiences/${farm.experienceSlug}`}
              className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-compass-3-line text-lg"></i>
              {farm.experienceLabel}
            </Link>
          )}
          {!open && onNotify && (
            <button
              type="button"
              onClick={onNotify}
              className="inline-flex items-center gap-2 bg-accent-500 text-background-50 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-notification-3-line text-lg"></i>
              Notify me when it reopens
            </button>
          )}
          <a
            href="#farm-location"
            className="inline-flex items-center gap-2 bg-background-50/90 text-foreground-900 border border-background-200 text-sm md:text-base font-semibold px-7 py-4 rounded-md hover:bg-background-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-map-pin-2-line text-lg"></i>
            See on Map
          </a>
        </div>
      </div>

      <div className="relative w-full px-4 md:px-8 pb-10 md:pb-14">
        <div className="bg-background-50/95 backdrop-blur-md rounded-lg border border-background-200 grid grid-cols-2 lg:grid-cols-4 divide-y-2 lg:divide-y-0 lg:divide-x divide-background-200">
          {facts.map((fact) => (
            <div key={fact.label} className="p-4 md:p-6 flex items-start gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 shrink-0">
                <i className={`${fact.icon} text-xl`}></i>
              </span>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
                  {fact.label}
                </p>
                <p className="text-sm font-medium text-foreground-900 mt-0.5">{fact.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}